// src/hooks/useTypeaheadSearch.js
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { db } from "@/firebase/config";
import {
  collection,
  getDocs,
  limit as qLimit,
  orderBy,
  query,
  startAt,
  endAt,
  documentId,
  where,
} from "@/lib/firestore-instrumented";

const DEFAULT_LIMIT = 5;
const DEFAULT_DEBOUNCE = 200;
const MIN_LEN = 2;

function prefixBounds(term = "") {
  const t = term.trim().toLowerCase();
  if (!t) return null;
  return { start: t, end: `${t}\uf8ff` };
}

function shapeUser(doc) {
  const data = doc.data() || {};
  return {
    id: doc.id,
    handle: data.username_lowercase || data.username || doc.id,
    displayName: data.displayName || "",
    avatar: data.profilePictureUrl || "",
    type: "user",
  };
}

function shapeAgent(doc) {
  const data = doc.data() || {};
  return {
    id: doc.id,
    handle: data.handle_lowercase || data.handle || doc.id,
    displayName: data.displayName || "",
    avatar: data.avatarUrl || "",
    status: data.status || "",
    type: "agent",
  };
}

function isPublicAgent(doc) {
  const data = doc.data() || {};
  return data.actorClass !== "synthetic_member" && data.visibleAsAgent !== false;
}

function shapeHub(doc) {
  const data = doc.data() || {};
  return {
    id: doc.id,
    slug: data.slug || doc.id,
    displayName: data.displayName || data.name || data.slug || doc.id,
    badgeUrl: data.badgeUrl || "",
    type: data.type || "",
    kind: "hub",
  };
}

async function fetchAllowedHubs(allowedHubSlugs, term, resultLimit) {
  const docs = [];
  for (let index = 0; index < allowedHubSlugs.length; index += 10) {
    const chunk = allowedHubSlugs.slice(index, index + 10);
    const snap = await getDocs(
      query(collection(db, "hubs"), where(documentId(), "in", chunk), qLimit(chunk.length))
    );
    docs.push(...snap.docs);
  }
  return docs
    .map(shapeHub)
    .filter((hub) => `${hub.slug} ${hub.displayName}`.toLowerCase().includes(term))
    .slice(0, resultLimit);
}

export function useTypeaheadSearch(options) {
  const {
    query: rawQuery,
    types = { users: true, agents: true, hubs: true },
    limit = DEFAULT_LIMIT,
    debounceMs = DEFAULT_DEBOUNCE,
    allowedHubSlugs = null,
  } = options || {};

  const [results, setResults] = useState({ users: [], agents: [], hubs: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const term = useMemo(() => (rawQuery || "").trim(), [rawQuery]);
  const timerRef = useRef(null);
  const lastTermRef = useRef("");

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!term || term.length < MIN_LEN) {
      setResults({ users: [], agents: [], hubs: [] });
      setLoading(false);
      setError("");
      return;
    }

    timerRef.current = setTimeout(async () => {
      lastTermRef.current = term;
      setLoading(true);
      setError("");
      try {
        const bounds = prefixBounds(term);
        if (!bounds) {
          setResults({ users: [], agents: [], hubs: [] });
          setLoading(false);
          return;
        }

        const promises = [];

        if (types.users) {
          const usersQ = query(
            collection(db, "users"),
            orderBy("username_lowercase"),
            startAt(bounds.start),
            endAt(bounds.end),
            qLimit(limit)
          );
          promises.push(
            getDocs(usersQ).then((snap) => snap.docs.map(shapeUser))
          );
        } else {
          promises.push(Promise.resolve([]));
        }

        if (types.agents) {
          const agentsQ = query(
            collection(db, "agents"),
            where("status", "==", "active"),
            orderBy("handle_lowercase"),
            startAt(bounds.start),
            endAt(bounds.end),
            qLimit(Math.max(limit, limit * 3))
          );
          promises.push(
            getDocs(agentsQ).then((snap) => snap.docs.filter(isPublicAgent).slice(0, limit).map(shapeAgent))
          );
        } else {
          promises.push(Promise.resolve([]));
        }

        if (types.hubs && Array.isArray(allowedHubSlugs)) {
          const normalizedTerm = bounds.start;
          promises.push(
            fetchAllowedHubs(allowedHubSlugs.slice(0, 50), normalizedTerm, limit)
          );
        } else if (types.hubs) {
          const hubsQ = query(
            collection(db, "hubs"),
            where("type", "in", ["public-view", "public-interact"]),
            orderBy("slug"),
            startAt(bounds.start),
            endAt(bounds.end),
            qLimit(limit)
          );
          promises.push(
            getDocs(hubsQ).then((snap) => snap.docs.map(shapeHub))
          );
        } else {
          promises.push(Promise.resolve([]));
        }

        const [users, agents, hubs] = await Promise.all(promises);

        // Ignore stale responses
        if (lastTermRef.current !== term) return;
        setResults({ users, agents, hubs });
      } catch (err) {
        if (lastTermRef.current !== term) return;
        setError(err?.message || "Search failed");
        setResults({ users: [], agents: [], hubs: [] });
      } finally {
        if (lastTermRef.current === term) {
          setLoading(false);
        }
      }
    }, debounceMs);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [term, types.users, types.agents, types.hubs, limit, debounceMs, allowedHubSlugs]);

  return { results, loading, error, hasQuery: term.length >= MIN_LEN };
}
